import { Group } from "konva/lib/Core";
import { Arrow } from "konva/lib/shapes/Arrow";
import { Circle } from "konva/lib/shapes/Circle";
import { Text } from "konva/lib/shapes/Text";
import { WhiteboardLink } from "./link";
import { nodeSize, Goal, Alert, isDraftOrImportedGoalState, scaleSize } from "../shapes";
import { fireStateEvent } from "../events";
import { WhiteboardLinkStateType } from ".";

export interface IAlignmentLinkSource {
  type: "goal" | "draftGoal" | "alert";
  id: string;
}

export interface IAlignmentLinkTarget {
  type: "goal" | "draftGoal" | "alert";
  id: string;
  metricId?: string;
}

export interface IAlignmentLinkState {
  type: "alignment";
  source: IAlignmentLinkSource;
  target: IAlignmentLinkTarget;
}

const eventName = (link: IAlignmentLinkState): string => `.alignment-link-${link.source.id}-${link.target.id}`;

export class AlignmentLink extends WhiteboardLink<IAlignmentLinkState> {
  private source: Goal | Alert;
  private target: Goal | Alert;

  static stateTypes: WhiteboardLinkStateType[] = ["alignment"];

  constructor(link: IAlignmentLinkState) {
    super(link);

    this.arrow();
    this.deleteIcon();
  }

  connectShapes(source: Goal | Alert, target: Goal | Alert): void {
    this.source = source;
    this.target = target;

    this.updatePoints();

    const updatePoints = () => this.updatePoints();
    (this.source as Group).on(`dragmove.${eventName(this.link)} resize.${eventName(this.link)}`, updatePoints);
    (this.target as Group).on(`dragmove.${eventName(this.link)} resize.${eventName(this.link)}`, updatePoints);
  }

  private arrow() {
    this.add(
      new Arrow({
        name: "arrow",
        fill: "#81ACFF",
        stroke: "#81ACFF",
        strokeWidth: 2,
        bezier: true,
        points: [],
      })
    );
  }

  private deleteIcon() {
    const group = new Group({ name: "delete" });
    group.add(
      new Circle({
        name: "circle",
        fill: "#81ACFF",
        stroke: "#81ACFF",
        radius: 7,
      })
    );
    group.add(
      new Text({
        name: "text",
        fontSize: 14,
        fontFamily: "Arial",
        fill: "#FFFFFF",
        text: "-",
        x: -2,
        y: -7,
      })
    );
    this.add(group);

    let previousCursor: string;
    group.on("mouseover", () => {
      previousCursor = document.body.style.cursor;
      document.body.style.cursor = "pointer";
    });
    group.on("mouseout", () => {
      document.body.style.cursor = previousCursor;
      previousCursor = undefined;
    });
    group.on("click", (evt) => {
      fireStateEvent("stateChange", evt, this.source, { state: this.source.state, changes: { parent: undefined } });
    });
  }

  destroy(): this {
    this.source.off(`dragmove.${eventName(this.link)} resize.${eventName(this.link)}`);
    this.target.off(`dragmove.${eventName(this.link)} resize.${eventName(this.link)}`);

    return super.destroy();
  }

  private updatePoints() {
    const stage = this.getStage();
    const stagePos = stage.position();
    const scale = stage.scale();

    // start from the middle left side of the source shape
    const sourceSize = scaleSize(scale, nodeSize(this.source));
    const sourceAbsPos = this.source.absolutePosition();
    const sourcePos = {
      x: (-1 * stagePos.x + sourceAbsPos.x) / scale.x - 10,
      y: (-1 * stagePos.y + sourceAbsPos.y) / scale.y + sourceSize.height / 2,
    };

    // finish at the middle right side of the target shape
    const targetAbs = this.target.absolutePosition();
    const targetSize = nodeSize(this.target);

    let targetAbsY = targetAbs.y;
    let targetHeight = targetSize.height;
    if (isDraftOrImportedGoalState(this.target.state)) {
      const row = this.link.target.metricId ? (this.target as Goal).metricRow(this.link.target.metricId) : (this.target as Goal).goalRow();
      if (row) {
        const rowSize = scaleSize(scale, nodeSize(row));
        targetHeight = rowSize.height;
        targetAbsY = row.absolutePosition().y;
      }
    }
    const targetPos = {
      x: (-1 * stagePos.x + targetAbs.x) / scale.x + targetSize.width + 10,
      y: (-1 * stagePos.y + targetAbsY) / scale.y + targetHeight / 2,
    };

    // 4-point bezier curve
    const x0 = sourcePos.x;
    const y0 = sourcePos.y;
    const x1 = (sourcePos.x + targetPos.x) / 2;
    const y1 = y0;
    const x2 = x1;
    const y2 = targetPos.y;
    const x3 = targetPos.x;
    const y4 = y2;
    this.findOne<Arrow>(".arrow").points([x0, y0, x1, y1, x2, y2, x3, y4]);

    this.findOne<Group>(".delete")
      .position({ x: x1, y: (sourcePos.y + targetPos.y) / 2 })
      .visible(this.link.source.type === "draftGoal" || this.link.target.type === "draftGoal");
  }
}
